import { useState } from "react";
import { TrendingUp, KeyRound, Sun, Calendar, Info, Calculator } from "lucide-react";
import { useLang } from "@/lib/lang-context";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Rental investment scenarios — long-term lease vs tourist short-term.
 *
 * Data comes from `analysis.rental_estimate` (backend `core/rental.py`).
 * Hides itself when `estimate` is null — backend already excluded
 * agricultural land, sub-25m² listings, and unknown locations.
 *
 * Why a tabbed UI
 * ---------------
 * Side-by-side cards would imply parity. Long-term and short-term are
 * fundamentally different commitments (occupation density, operational
 * load, tax regime). Tabs make the comparison explicit without forcing
 * a layout shouting "they're equivalent".
 */

const fmtEUR = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
      }).format(n);

const fmtPct = (n) => (n == null ? "—" : `${n.toFixed(2)}%`);

const SOURCE_BADGE_KEY = {
  town: "rental.sourceTown",
  town_partial: "rental.sourceTownPartial",
  province_fallback: "rental.sourceProvince",
};

// Tier-specific badge shown on the tourist tab. "baseline" gets the most
// cautious copy so users in inland towns understand the number is a
// conservative estimate, not a hot-market promise.
const TIER_BADGE_KEY = {
  strong: "rental.tierStrong",
  moderate: "rental.tierModerate",
  baseline: "rental.tierBaseline",
};

const TIER_BADGE_CLASS = {
  strong: "bg-[#4A5D23] text-white border-[#3B4A1C]",
  moderate: "bg-[#FAF9F4] text-[#282C2A] border-[#282C2A]",
  baseline: "bg-white text-[#6C706D] border-[#E6E4DD]",
};

/**
 * Realistic-net-yield summary box. Lives at the top of the long-term
 * tab and rolls up gross rent minus the four operating costs the rest
 * of the report estimates (IMU, condo, maintenance, vacancy, mgmt).
 *
 * Visually distinct (olive-leaning) so investors immediately see "this
 * is the number that matters" without having to compare three blocks.
 *
 * `condoIncluded` (toggled upstream by `CondoFees`) zeroes-out the
 * condo line and re-derives the net yield % live so the bottom-line
 * conclusion stays consistent with what the user just chose.
 */
const NetYieldBox = ({ netYield, condoIncluded = true }) => {
  const { t } = useLang();
  if (!netYield) return null;

  // Recompute the totals if the user has flipped "Non previste":
  // - drop the condo cost line from the visible breakdown
  // - boost annual_net by that exact amount
  // - re-derive net_yield_pct from the new annual_net / asking_price.
  const condoCost = condoIncluded ? (netYield.cost_condo_eur || 0) : 0;
  const totalCosts =
    (netYield.cost_imu_eur || 0) +
    condoCost +
    (netYield.cost_maintenance_eur || 0) +
    (netYield.cost_vacancy_eur || 0) +
    (netYield.cost_management_eur || 0);
  const annualNet = (netYield.annual_gross_eur || 0) - totalCosts;
  // The backend stored the asking price implicitly via net/yield: derive
  // it back to get a stable denominator that's identical to the original.
  const askingPrice =
    netYield.net_yield_pct && netYield.annual_net_eur
      ? netYield.annual_net_eur / (netYield.net_yield_pct / 100)
      : null;
  const netYieldPct = askingPrice ? (annualNet / askingPrice) * 100 : netYield.net_yield_pct;
  const payback = annualNet > 0 && askingPrice ? Math.round((askingPrice / annualNet) * 10) / 10 : null;
  const positive = netYieldPct > 0;
  return (
    <div
      data-testid="net-yield-box"
      className="mt-8 border-2 border-[#4A5D23] bg-[#FAFBF6] p-5 md:p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="bg-[#4A5D23] text-white p-2 mt-1">
            <Calculator size={16} strokeWidth={1.5} />
          </div>
          <div>
            <div className="label-eyebrow text-[#4A5D23]">{t("netYield.eyebrow")}</div>
            <h4 className="font-serif-display text-2xl mt-1 text-[#282C2A]">
              {t("netYield.title")}
            </h4>
          </div>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">{t("netYield.yield")}</div>
          <div
            data-testid="net-yield-pct"
            className={`font-serif-display text-4xl mt-1 ${
              positive ? "text-[#4A5D23]" : "text-[#A0522D]"
            }`}
          >
            {netYieldPct?.toFixed(2)}%
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <div className="label-eyebrow">{t("netYield.gross")}</div>
          <div data-testid="net-yield-gross" className="font-mono text-base mt-1 text-[#282C2A]">
            {fmtEUR(netYield.annual_gross_eur)}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">{t("netYield.totalCosts")}</div>
          <div className="font-mono text-base mt-1 text-[#A0522D]">
            −{fmtEUR(totalCosts)}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">{t("netYield.net")}</div>
          <div data-testid="net-yield-eur" className="font-mono text-base mt-1 text-[#282C2A] font-semibold">
            {fmtEUR(annualNet)}
          </div>
        </div>
        <div>
          <div className="label-eyebrow">{t("netYield.payback")}</div>
          <div
            data-testid="net-yield-payback"
            className="font-mono text-base mt-1 text-[#282C2A]"
          >
            {payback
              ? `${payback} ${t("netYield.years")}`
              : "—"}
          </div>
        </div>
      </div>

      {/* Cost breakdown — small + plain so the box stays scannable.
          When the user toggled "Non previste", the condo line is muted
          to make the change visible at a glance. */}
      <div
        data-testid="net-yield-breakdown"
        className="mt-5 pt-4 border-t border-[#E6E4DD] grid grid-cols-2 md:grid-cols-5 gap-3 font-mono text-xs"
      >
        <div>
          <span className="text-[#6C706D]">{t("netYield.imu")}: </span>
          <span className="text-[#A0522D]">−{fmtEUR(netYield.cost_imu_eur)}</span>
        </div>
        <div className={condoIncluded ? "" : "opacity-40 line-through"}>
          <span className="text-[#6C706D]">{t("netYield.condo")}: </span>
          <span className="text-[#A0522D]">
            −{fmtEUR(condoIncluded ? netYield.cost_condo_eur : 0)}
          </span>
        </div>
        <div>
          <span className="text-[#6C706D]">{t("netYield.maintenance")}: </span>
          <span className="text-[#A0522D]">−{fmtEUR(netYield.cost_maintenance_eur)}</span>
        </div>
        <div>
          <span className="text-[#6C706D]">{t("netYield.vacancy")}: </span>
          <span className="text-[#A0522D]">−{fmtEUR(netYield.cost_vacancy_eur)}</span>
        </div>
        <div>
          <span className="text-[#6C706D]">{t("netYield.management")}: </span>
          <span className="text-[#A0522D]">−{fmtEUR(netYield.cost_management_eur)}</span>
        </div>
      </div>

      {netYield.assumptions?.length > 0 && (
        <ul className="mt-4 text-[11px] text-[#6C706D] space-y-1">
          {netYield.assumptions.map((a, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-[#A0A39B]">·</span>
              <span>{a}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const RentalAnalysis = ({ estimate, bonus = 0, netYield = null, condoIncluded = true }) => {
  const { t } = useLang();
  const [active, setActive] = useState("longterm");

  if (!estimate) return null;

  const { longterm, tourist, monthly_rate_eur_sqm, rate_source } = estimate;
  const scenario = active === "tourist" && tourist ? tourist : longterm;
  const isTourist = scenario.kind === "tourist";

  const sourceLabel = SOURCE_BADGE_KEY[rate_source?.kind] || "rental.sourceTown";

  return (
    <div
      data-testid="rental-analysis"
      className="px-6 md:px-10 py-8 border-t border-[#E6E4DD] bg-white"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <TrendingUp size={12} strokeWidth={1.5} />
            {t("rental.title")}
          </div>
          <p className="mt-2 text-sm text-[#6C706D] max-w-2xl">{t("rental.sub")}</p>
          {/* Potential rental income score — separated from the headline deal score
              by design. Investor-specific signal, not property-intrinsic quality.
              Always rendered (even at 0) so users see the indicator exists. */}
          <div
            data-testid="rental-bonus-badge"
            className="mt-4 inline-flex items-center gap-3 border border-[#E6E4DD] bg-[#FAF9F4] px-3 py-2"
          >
            <span className="label-eyebrow">{t("rental.bonusLabel")}</span>
            <span
              data-testid="rental-bonus-value"
              className={`font-serif-display text-2xl leading-none ${
                bonus > 0 ? "text-[#4A5D23]" : "text-[#6C706D]"
              }`}
            >
              {bonus > 0 ? `+${bonus}` : bonus}
              <span className="text-xs font-mono text-[#6C706D] ml-0.5">/3</span>
            </span>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={t("rental.bonusLabel")}
                    data-testid="rental-bonus-info"
                    className="inline-flex h-4 w-4 items-center justify-center text-[#A6AAA7] hover:text-[#282C2A] focus:outline-none focus:text-[#4A5D23] transition-colors"
                  >
                    <Info size={12} strokeWidth={1.5} />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="right"
                  className="max-w-[280px] bg-[#282C2A] text-white border-0 px-3 py-2 text-[11px] leading-snug font-normal normal-case tracking-normal"
                >
                  {t("sheet.rules.rental")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">{t("rental.baseRate")}</div>
          <div
            className="font-mono text-base mt-1 text-[#282C2A]"
            data-testid="rental-base-rate"
          >
            € {monthly_rate_eur_sqm.toFixed(2)}/m²/{t("rental.monthShort")}
          </div>
          <div className="font-mono text-[10px] text-[#6C706D] uppercase tracking-widest mt-0.5">
            {t(sourceLabel)}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="inline-flex border border-[#E6E4DD] bg-[#FAF9F4]">
          <ScenarioTab
            active={active === "longterm"}
            onClick={() => setActive("longterm")}
            icon={KeyRound}
            label={t("rental.longterm")}
            testId="rental-tab-longterm"
          />
          <ScenarioTab
            active={active === "tourist"}
            onClick={() => setActive("tourist")}
            icon={Sun}
            label={t("rental.tourist")}
            testId="rental-tab-tourist"
          />
        </div>
        {/* Tier badge — only meaningful on the tourist tab, set expectations
            for inland towns where the model leans on conservative defaults. */}
        {isTourist && scenario.market_tier && (
          <span
            data-testid="rental-tier-badge"
            className={`inline-flex items-center px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider border ${
              TIER_BADGE_CLASS[scenario.market_tier] || TIER_BADGE_CLASS.baseline
            }`}
          >
            {t(TIER_BADGE_KEY[scenario.market_tier] || "rental.tierBaseline")}
          </span>
        )}
      </div>

      {/* Headline numbers — net yield + payback (the only two metrics most investors actually care about) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <HeadlineMetric
          label={t("rental.netYield")}
          value={fmtPct(scenario.net_yield_pct)}
          accent="olive"
          testId="rental-net-yield"
          sub={`${t("rental.grossYield")}: ${fmtPct(scenario.gross_yield_pct)}`}
        />
        <HeadlineMetric
          label={t("rental.payback")}
          value={
            scenario.payback_years == null
              ? "—"
              : `${scenario.payback_years.toFixed(1)} ${t("rental.years")}`
          }
          accent="ink"
          testId="rental-payback"
          sub={t("rental.paybackHint")}
        />
      </div>

      {/* Breakdown */}
      <dl
        data-testid={`rental-breakdown-${scenario.kind}`}
        className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm pt-5 border-t border-[#E6E4DD]"
      >
        {isTourist ? (
          <>
            <BreakdownItem
              label={t("rental.peakWeekly")}
              value={fmtEUR(scenario.peak_weekly_eur)}
              icon={Sun}
            />
            <BreakdownItem
              label={t("rental.seasonWeeks")}
              value={`${scenario.high_season_weeks} ${t("rental.weeks")}`}
              icon={Calendar}
            />
          </>
        ) : (
          <BreakdownItem
            label={t("rental.monthlyGross")}
            value={fmtEUR(scenario.monthly_gross_eur)}
            icon={Calendar}
            wide
          />
        )}
        <BreakdownItem
          label={t("rental.annualGross")}
          value={fmtEUR(scenario.annual_gross_eur)}
        />
        <BreakdownItem
          label={`${t("rental.annualNet")} (${t("rental.costsLabel")} ${scenario.cost_ratio_pct}%)`}
          value={fmtEUR(scenario.annual_net_eur)}
          emphasis
        />
      </dl>

      <p className="mt-6 text-[11px] text-[#6C706D] flex items-start gap-1.5 max-w-3xl">
        <Info size={11} strokeWidth={1.5} className="mt-0.5 shrink-0" />
        <span>
          {isTourist
            ? scenario.market_tier === "baseline"
              ? t("rental.disclaimerTouristBaseline")
              : t("rental.disclaimerTourist")
            : t("rental.disclaimerLongterm")}
        </span>
      </p>

      {/* Realistic net yield summary — placed at the END of the long-term
          tab as a "bottom-line" conclusion that ties together IMU + condo
          + maintenance + vacancy + management. Tourist tab keeps its own
          dedicated cost mix and skips this block. */}
      {!isTourist && netYield && <NetYieldBox netYield={netYield} condoIncluded={condoIncluded} />}
    </div>
  );
};

const ScenarioTab = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
      active
        ? "bg-[#282C2A] text-white"
        : "bg-transparent text-[#282C2A] hover:bg-[#E6E4DD]"
    }`}
  >
    <Icon size={13} strokeWidth={1.5} />
    {label}
  </button>
);

const HeadlineMetric = ({ label, value, accent, sub, testId }) => {
  const accentClass = accent === "olive" ? "text-[#4A5D23]" : "text-[#282C2A]";
  return (
    <div className="border border-[#E6E4DD] p-5">
      <div className="label-eyebrow">{label}</div>
      <div
        className={`font-serif-display text-4xl md:text-5xl leading-none mt-2 ${accentClass}`}
        data-testid={testId}
      >
        {value}
      </div>
      {sub && (
        <div className="font-mono text-[10px] text-[#6C706D] uppercase tracking-widest mt-3">
          {sub}
        </div>
      )}
    </div>
  );
};

const BreakdownItem = ({ label, value, icon: Icon, emphasis, wide }) => (
  <div className={`flex flex-col ${wide ? "col-span-2" : ""}`}>
    <dt className="label-eyebrow inline-flex items-center gap-1.5">
      {Icon && <Icon size={11} strokeWidth={1.5} />}
      {label}
    </dt>
    <dd
      className={`font-mono mt-1 ${
        emphasis ? "text-base text-[#4A5D23] font-semibold" : "text-sm text-[#282C2A]"
      }`}
    >
      {value}
    </dd>
  </div>
);

export default RentalAnalysis;
